/**
 * Where Sabine answers, one row per surface (SAL-3310). `runSabine`
 * (lib/sabine/runtime.js) reads everything that differs between surfaces
 * from here, so a new surface is a row and a thin adapter that calls it.
 *
 * - `channel`: "chat" streams the reply, "email" returns it whole; her
 *   identity writes for it (`buildSabineIdentity`). A chat row with
 *   `stream: false` returns the reply whole too, for a page that reads one
 *   JSON reply instead of a stream.
 * - `identity`: finishes "You are Sabine, Salesably's …, <identity>.".
 * - `note`: the standing instructions she reads here (lib/sabine/notes.js).
 * - `coreTools`: the most she may call here. The member's role allow-list
 *   (lib/agent-tools/external-agent-access.js) narrows it; null is the whole
 *   role list. `readOnly` drops any core tool that writes.
 * - `maxSteps`, `timeoutMs`: model steps a turn may take, and how long it may
 *   run before it is aborted.
 * - `agentSource`: the surface's name in the capability audit log.
 * - `feedback`: the AIChatFeedbackSource each turn is filed under, or null
 *   for a surface that records none or, as a shared chat, files each thread
 *   by the record it is about.
 * - `label`: the surface as a sentence names it ("the reports chat").
 * - `recordsThread`: whether the surface keeps its thread in Sabine's one
 *   store (lib/sabine-chat/service.js), which is all `recall_recent_threads`
 *   can read back (SAL-3382). A shared chat keeps its own thread, the studio
 *   setup and content builder pages keep none, and a conversation held in
 *   Claude or ChatGPT is not a surface at all, so none of those is recalled.
 *
 * Client components read the feedback row, so nothing here may import the
 * database or a server-only module.
 */
import { CONTENT_BUILDER_TOOLS } from "@/lib/ai-content-builder/program-agent";
import { SEARCH_PLAYBOOK_TOOL } from "@/lib/playbook/chat-messages";
import { getSabineSources } from "@/lib/sabine-chat/sources";

// Her memory across surfaces (SAL-3310): the member's recent threads in this
// studio, mounted on every chat surface that is not about one page's content.
// The recalled threads are material, never instructions (lib/sabine/notes.js).
const RECALL_TOOL = "recall_recent_threads";

// The bridge emits catalog order, independent of the question, so the tool
// schemas stay a cacheable prefix.
const HOME_CORE_TOOLS = Object.freeze([
  "salesably_get_my_practice_summary",
  "salesably_list_my_learning_paths",
  "salesably_list_my_due_work",
  SEARCH_PLAYBOOK_TOOL,
  "salesably_get_studio_context",
  "salesably_list_studio_content",
  "salesably_find_page",
  "salesably_get_team_overview",
  RECALL_TOOL,
]);

const HOME = {
  name: "home",
  label: "the home page",
  channel: "chat",
  identity: "answering open-ended questions on the home page",
  note: "home",
  coreTools: HOME_CORE_TOOLS,
  readOnly: true,
  recordsThread: true,
  maxSteps: 8,
  // Under the home and mobile routes' 60 s maxDuration, so the turn ends as an abort
  // rather than the platform killing the function mid-stream.
  timeoutMs: 55_000,
  agentSource: "home-sabine",
  feedback: { source: "HOME_SABINE", contextType: "HOME_CHAT" },
};

// Ask Studio on the reports pages: a custom report, the built-in page that
// answers instead, the page directory, and the two team snapshots home also
// has. The nudge sender is a write, so the read-only surface never mounts it;
// the health card's own buttons send reminders.
const REPORTS_CORE_TOOLS = Object.freeze([
  "find_report_page",
  "run_report",
  "salesably_find_page",
  "salesably_get_team_overview",
  "salesably_get_assignment_health_autopilot",
  RECALL_TOOL,
]);

const REPORTS = {
  name: "reports",
  label: "the reports chat",
  channel: "chat",
  identity: "answering a manager's questions about their studio's reporting data",
  note: "reports",
  coreTools: REPORTS_CORE_TOOLS,
  readOnly: true,
  recordsThread: true,
  maxSteps: 6,
  // One run_report call is the SQL loop's 90 s deadline plus the 30 s reply
  // formatter; under the reports route's 180 s maxDuration.
  timeoutMs: 170_000,
  agentSource: "reports-sabine",
  feedback: { source: "REPORTS", contextType: "REPORTS_CHAT" },
};

// The playbook page: one tool, which reads the studio's playbook on the
// server for each request, so the surface needs no other and a public link's
// reader (a member on the role list) may call it.
const PLAYBOOK = {
  name: "playbook",
  label: "the playbook",
  channel: "chat",
  identity: "answering questions about the sales playbook the user is reading",
  note: "playbook",
  coreTools: Object.freeze([SEARCH_PLAYBOOK_TOOL]),
  readOnly: true,
  recordsThread: true,
  maxSteps: 4,
  // Under the playbook route's 60 s maxDuration, as on home.
  timeoutMs: 55_000,
  agentSource: "playbook-sabine",
  feedback: { source: getSabineSources("PLAYBOOK").feedback, contextType: "PLAYBOOK" },
};

// The studio setup page: the setup tools, which read and advance the page's
// setup state (lib/agent-tools/studio-setup-tool-definitions.js), the
// catalog tools that already revise positioning and read saved studio
// content, and the page directory. The page reads one JSON reply per turn.
const STUDIO_SETUP_CORE_TOOLS = Object.freeze([
  "studio_setup_saveWebsite",
  "studio_setup_findLogoCandidate",
  "studio_setup_saveLogo",
  "studio_setup_confirmNoDocuments",
  "studio_setup_jumpToStep",
  "studio_setup_skipOptionalStep",
  "studio_setup_generatePositioning",
  "studio_setup_generateContent",
  "studio_setup_addRecommendedResources",
  "salesably_update_studio_positioning",
  "salesably_get_studio_context",
  "salesably_find_page",
]);

const STUDIO_SETUP = {
  name: "studio-setup",
  label: "the studio setup page",
  channel: "chat",
  stream: false,
  identity: "setting up a studio admin's new studio with them, one step at a time",
  note: "studio-setup",
  coreTools: STUDIO_SETUP_CORE_TOOLS,
  readOnly: false,
  recordsThread: false,
  maxSteps: 8,
  // Under the setup route's 120 s maxDuration: a step may generate and save
  // a batch of content.
  timeoutMs: 110_000,
  agentSource: "studio-setup-sabine",
  feedback: null,
};

// The content builder dialog: her two drafting tools, which read the run the
// route put on the tool context and send the page each draft
// (lib/agent-tools/content-builder-tool-definitions.js), and the two studio
// reads the dialog's "include studio context" switch may leave out for a
// run. Every tool is read-only: a draft goes to the page for review and is
// saved from there. The page reads the run as the events the tools send, so
// the reply itself is returned whole.
const CONTENT_BUILDER = {
  name: "content-builder",
  label: "the content builder",
  channel: "chat",
  stream: false,
  identity: "drafting studio content for an admin to review",
  note: "content-builder",
  coreTools: Object.freeze([
    ...CONTENT_BUILDER_TOOLS,
    "salesably_get_studio_context",
    "salesably_list_studio_content",
  ]),
  readOnly: true,
  recordsThread: false,
  maxSteps: 10,
  // Under the builder route's 300 s maxDuration: several content types may
  // be drafted back to back.
  timeoutMs: 240_000,
  agentSource: "content-builder-sabine",
  feedback: null,
};

// A coach page pins its record into the prompt (lib/result-coach/records.js),
// so the page's own question needs no tool; these are for what lies outside
// it.
const COACH_CORE_TOOLS = Object.freeze([
  "salesably_find_page",
  "salesably_recommend_next_simulations",
  RECALL_TOOL,
]);

// The coach beside one saved record. Every record kind reads the same note,
// which branches on the pinned record (lib/sabine/notes.js).
const COACH = {
  channel: "chat",
  identity: "coaching a seller on one saved result: you help them understand their feedback and plan their next reps",
  note: "coach",
  coreTools: COACH_CORE_TOOLS,
  readOnly: true,
  maxSteps: 4,
  // Under the coach routes' 60 s maxDuration, as on home.
  timeoutMs: 55_000,
};

// A coach page files its turns where every chat about its kind of record is
// filed (lib/sabine-chat/sources.js), and keeps its thread there; a coach
// surface with no record kind (a shared chat) keeps its own.
const coachSurface = (name, label, agentSource, recordKind) =>
  Object.freeze({
    ...COACH,
    name,
    label,
    agentSource,
    recordsThread: Boolean(recordKind),
    feedback: recordKind ? { source: getSabineSources(recordKind).feedback } : null,
  });

export const SABINE_SURFACES = Object.freeze({
  home: Object.freeze(HOME),
  // The iOS and Android Sabine tab: the home chat behind a bearer token. Its
  // route saves each turn into the studio's home thread (SAL-3310); the chat
  // capture still writes through the browser session it does not have.
  mobile: Object.freeze({ ...HOME, name: "mobile", label: "the mobile app", agentSource: "mobile-sabine", feedback: null }),
  reports: Object.freeze(REPORTS),
  playbook: Object.freeze(PLAYBOOK),
  "studio-setup": Object.freeze(STUDIO_SETUP),
  "content-builder": Object.freeze(CONTENT_BUILDER),
  email: Object.freeze({
    name: "email",
    label: "email",
    channel: "email",
    identity: null,
    note: "email",
    coreTools: null,
    readOnly: false,
    recordsThread: true,
    maxSteps: 12,
    timeoutMs: 100_000,
    agentSource: "email-agent",
    feedback: { source: "EMAIL" },
  }),
  // The three coach pages.
  result: coachSurface("result", "a result page", "result-coach", "SIMULATION_RESULT"),
  transcript: coachSurface("transcript", "a transcript page", "transcript-coach", "TRANSCRIPT_RESULT"),
  // A Deal Clinic readout is a live deal, not practice (SAL-3316): its own
  // identity, and no practice recommender, which reads a practice result.
  readout: Object.freeze({
    ...coachSurface("readout", "a Deal Clinic readout", "readout-coach", "COG_REPORT"),
    identity: "coaching a manager or rep on one Deal Clinic readout: you help them read the diagnosis of a live deal and plan its next move",
    coreTools: COACH_CORE_TOOLS.filter((name) => name !== "salesably_recommend_next_simulations"),
  }),
  // A coach thread reopened from a share link: the record as it was shared,
  // filed under the source of the kind of record it is about.
  "shared-chat": coachSurface("shared-chat", "a shared chat", "shared-chat", null),
});

/** The row for `name`; throws on a surface nothing defines. */
export function getSabineSurface(name) {
  const surface = SABINE_SURFACES[name];
  if (!surface) throw new Error(`Unknown Sabine surface "${name}"`);
  return surface;
}

/** The surfaces whose threads `recall_recent_threads` can read back, in row order. */
export const THREAD_SURFACES = Object.freeze(
  Object.values(SABINE_SURFACES).filter((row) => row.recordsThread),
);

/** Those surfaces as a sentence lists them: "the home page, the mobile app, … and email". */
export function describeThreadSurfaces() {
  const labels = THREAD_SURFACES.map((row) => row.label);
  return `${labels.slice(0, -1).join(", ")} and ${labels.at(-1)}`;
}

/** What recall says of a conversation held in Claude or ChatGPT: it is not kept, so it is never recalled. */
export const CONNECTOR_THREADS_NOT_KEPT =
  "A conversation held in Claude or ChatGPT is not kept, so it is never recalled.";
